/**
 * The Talent Community email: one FINN-branded layout for campaigns and the automated GDPR mails,
 * ported from Anna's mockup v0.5 (`brandedTemplate` / `exportEmailHtml`, design v0.6 decision 19).
 *
 * ONE source of truth: the editor shows exactly this HTML, and `exportEmailHtml` turns that same DOM
 * into the email-client-safe HTML that gets sent — never hand-built HTML. Editable regions carry
 * `contenteditable`; buttons and links carry `data-btn` / `data-link` so the export knows what they
 * are without guessing from their styles.
 */

export type EmailKind = 'rolefit' | 'newsletter' | 'consent';
export type EmailLang = 'en' | 'de';

export interface TemplateMeta {
  key: string;
  name: string;
  subject: string;
  intro: string;
  kind: EmailKind;
  lang: EmailLang;
  /** Sent by the tool, not by a recruiter (the GDPR renewal). */
  automated?: boolean;
}

/** Merge tokens a recruiter may type or insert. Filled per candidate at send time. */
export const TOKENS: [string, string][] = [
  ['{{first_name}}', 'Candidate’s first name'],
  ['{{role_title}}', 'The role they were archived from'],
  ['{{recruiter_name}}', 'You, the campaign owner'],
];

/**
 * Two campaign types in English and German, and the consent renewal. "It takes one click" from the
 * mockup is gone (open item #19): the click opens the candidate's page, where they confirm.
 */
export const TEMPLATES: TemplateMeta[] = [
  {
    key: 'rolefit',
    name: 'EN Role Campaign',
    subject: 'A role at FINN that fits you',
    intro:
      'Hi {{first_name}}, headcount for {{role_title}} just reopened, and you were one of the strongest candidates we saw for it. Would you be open to picking the conversation back up?',
    kind: 'rolefit',
    lang: 'en',
  },
  {
    key: 'newsletter',
    name: 'EN Company Update',
    subject: 'An update from FINN',
    intro:
      'Hi {{first_name}}, here is a quick look at what is happening across FINN right now, and a few roles we are excited to fill.',
    kind: 'newsletter',
    lang: 'en',
  },
  {
    key: 'rolefit_de',
    name: 'DE Rollen-Kampagne',
    subject: 'Eine Rolle bei FINN, die zu dir passt',
    intro:
      'Hallo {{first_name}}, die Position für {{role_title}} ist wieder offen, und du warst eine der stärksten Kandidat:innen, die wir dafür gesehen haben. Hättest du Lust, das Gespräch wieder aufzunehmen?',
    kind: 'rolefit',
    lang: 'de',
  },
  {
    key: 'newsletter_de',
    name: 'DE Unternehmens-Update',
    subject: 'Neuigkeiten von FINN',
    intro:
      'Hallo {{first_name}}, hier ein kurzer Überblick, was gerade bei FINN passiert, und ein paar Rollen, die wir aktuell besetzen möchten.',
    kind: 'newsletter',
    lang: 'de',
  },
  {
    key: 'consent_renewal',
    name: 'EN Consent Renewal',
    subject: 'Your consent to stay in the FINN Talent Community expires soon',
    intro:
      'Hi {{first_name}}, you agreed to let FINN keep your details in the FINN Talent Community, and that consent expires on {{consent_expiry}}. Want to stay? Confirm below. If you would rather not, tell us and we will delete your data.',
    kind: 'consent',
    lang: 'en',
    automated: true,
  },
  {
    key: 'consent_renewal_de',
    name: 'DE Einwilligungs-Erneuerung',
    subject: 'Deine Einwilligung für die FINN Talent Community läuft bald ab',
    intro:
      'Hallo {{first_name}}, du hast eingewilligt, dass FINN deine Daten in der FINN Talent Community speichert. Diese Einwilligung läuft am {{consent_expiry}} ab. Möchtest du bleiben? Bestätige es unten. Falls nicht, sag uns Bescheid, dann löschen wir deine Daten.',
    kind: 'consent',
    lang: 'de',
    automated: true,
  },
];

export const templateByKey = (key: string) => TEMPLATES.find((t) => t.key === key) || TEMPLATES[0];

/** Where each button points, in order, once exported. */
const LINKS: Record<EmailKind, string[]> = {
  consent: ['{{consent_yes_link}}', '{{consent_no_link}}'],
  newsletter: ['{{open_roles_link}}'],
  rolefit: ['{{interested_link}}', '{{not_now_link}}'],
};

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}

const F = 'font-family:Helvetica,Arial,sans-serif';
const PRIMARY = `display:inline-block;background:#0072EA;color:#FFFFFF;${F};font-weight:600;font-size:14px;padding:13px 26px;border-radius:2px`;
const SECONDARY = `display:inline-block;background:#FFFFFF;color:#191919;border:1px solid #9AA0AD;${F};font-weight:600;font-size:14px;padding:12px 25px;border-radius:2px`;
const LINK = 'color:#0072EA;text-decoration:underline';

const edit = (tag: 'div' | 'span', style: string, html: string, extra = '') =>
  `<${tag} contenteditable="true"${extra} style="${style}">${html}</${tag}>`;
const btn = (kind: 'primary' | 'secondary', label: string) =>
  edit('span', kind === 'primary' ? PRIMARY : SECONDARY, label, ` data-btn="${kind}"`);
const portalLink = (label: string) => `<span data-link="portal" style="${LINK}">${label}</span>`;

/**
 * The editor HTML for one email. `subject` and `signerName` are user input and escaped here; the
 * template texts are our own constants.
 */
export function buildEmailHtml(meta: TemplateMeta, opts: { subject?: string; signerName?: string } = {}): string {
  const de = meta.lang === 'de';
  const subject = escapeHtml((opts.subject ?? meta.subject).trim() || (de ? 'Neuigkeiten von FINN' : 'An update from FINN'));
  const signer = escapeHtml(opts.signerName?.trim() || '{{recruiter_name}}');
  const consent = meta.kind === 'consent';
  const badge = consent
    ? de ? 'Datenschutz' : 'Data privacy'
    : meta.kind === 'newsletter'
      ? de ? 'Unternehmens-Update' : 'Company Update'
      : 'Talent Community';

  const pair = (a: string, b: string) =>
    `<tr><td style="padding:26px 30px 0;text-align:center"><table role="presentation" cellpadding="0" cellspacing="0" align="center" style="margin:0 auto"><tr><td style="padding:0 5px">${btn('primary', a)}</td><td style="padding:0 5px">${btn('secondary', b)}</td></tr></table></td></tr>`;
  const cta = consent
    ? pair(de ? 'Ja, in der Talent Community bleiben' : 'Yes, keep me in the Talent Community', de ? 'Nein, Daten löschen' : 'No, delete my data')
    : meta.kind === 'newsletter'
      ? `<tr><td style="padding:26px 30px 0;text-align:center">${btn('primary', de ? 'Offene Stellen ansehen →' : 'Explore open roles →')}</td></tr>`
      : pair(de ? 'Interessiert' : 'Interested', de ? 'Aktuell nicht interessiert' : 'Not right now');

  const expiryBox = consent
    ? `<tr><td style="padding:22px 30px 0">${edit(
        'div',
        `${F};font-size:14px;line-height:150%;font-weight:400;color:#191919;background:#F5F5F5;border-left:3px solid #0072EA;padding:14px 16px`,
        de
          ? '<b style="font-weight:600">Deine Einwilligung läuft am {{consent_expiry}} ab.</b> Antwortest du bis dahin nicht, löschen wir deine Daten automatisch und vollständig.'
          : '<b style="font-weight:600">Your consent expires on {{consent_expiry}}.</b> If you don’t respond by then, we will delete your data automatically and completely.'
      )}</td></tr>`
    : '';
  const lookFirst = consent
    ? `<tr><td style="padding:22px 30px 0;text-align:center">${edit(
        'div',
        `${F};font-size:13px;line-height:150%;font-weight:300;color:#191919`,
        de
          ? `Erst alles ansehen? Verwalte deine Daten und Kontaktwünsche in deinem ${portalLink('Kandidat:innen-Profil')}.`
          : `Want to look first? Manage your data and contact preferences in your ${portalLink('candidate profile')}.`
      )}</td></tr>`
    : '';
  const optOut = consent
    ? de
      ? `Du erhältst diese E-Mail, weil du in die Speicherung deiner Daten in der FINN Talent Community eingewilligt hast (Art. 6 Abs. 1 lit. a DSGVO). Du kannst deine Einwilligung jederzeit in deinem ${portalLink('Kandidat:innen-Profil')} widerrufen.`
      : `You are receiving this because you consented to FINN storing your details in the Talent Community (Art. 6(1)(a) GDPR). You can withdraw your consent at any time in your ${portalLink('candidate profile')}.`
    : de
      ? `Möchtest du deine Angaben ändern oder dich abmelden? ${portalLink('Zu deinem Kandidat:innen-Profil')}.`
      : `Want to update your details or opt out of hearing from us? ${portalLink('Visit your candidate profile')}.`;

  return (
    `<table role="presentation" data-kind="${meta.kind}" width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;margin:0 auto;background:#FFFFFF;${F};border-collapse:collapse"><tbody>` +
    `<tr><td style="padding:22px 30px;border-bottom:1px solid #E9EAEC;${F};font-size:19px;font-weight:700;color:#191919;letter-spacing:-.5px">FINN</td></tr>` +
    `<tr><td data-hero="" style="padding:0;background:#191919;height:190px"></td></tr>` +
    `<tr><td style="padding:28px 30px 0">${edit('span', `display:inline-block;background:#E6F1FD;color:#0072EA;${F};font-size:11px;font-weight:600;padding:4px 10px;border-radius:2px`, badge)}</td></tr>` +
    `<tr><td style="padding:14px 30px 0">${edit('div', `${F};color:#191919;font-size:32px;line-height:125%;font-weight:700`, subject)}</td></tr>` +
    `<tr><td style="padding:14px 30px 0">${edit('div', `${F};color:#191919;font-size:15px;line-height:150%;font-weight:300`, escapeHtml(meta.intro))}</td></tr>` +
    expiryBox +
    cta +
    lookFirst +
    `<tr><td style="padding:34px 30px 0"><div style="height:1px;background:#E9EAEC;line-height:1px;font-size:1px">&nbsp;</div></td></tr>` +
    `<tr><td style="padding:22px 30px 0">${edit('div', `${F};font-size:14px;color:#191919`, `<div style="font-weight:300;margin-bottom:4px">${de ? 'Viele Grüße' : 'Best regards'}</div><div><b style="font-weight:600">${signer}</b><br><span style="font-weight:300">Talent Acquisition, FINN</span></div>`)}</td></tr>` +
    `<tr><td style="padding:20px 30px 28px">${edit('div', `${F};font-size:12px;line-height:150%;color:#6B6B6B`, optOut)}</td></tr>` +
    `<tr><td style="padding:26px 30px 30px;background:#191919">` +
    edit('div', `${F};font-size:13px;font-weight:600;color:#FFFFFF;margin-bottom:10px`, 'Einfach Auto fahren') +
    edit('div', `${F};font-size:11px;line-height:165%;color:#9AA0AD`, 'FINN GmbH<br>Prinzregentenplatz 9, 81675 München, Bayern<br><br>© 2026 FINN GmbH<br>HRB 249292 AG München | USt-ID DE 324875002<br>Maximilian Wühr | Jürgen Lobach') +
    `</td></tr></tbody></table>`
  );
}

/** Only hosted https images work in Gmail (it blocks data URIs). */
export const isHostedImage = (url: string) => /^https:\/\/[^\s"'<>]+$/i.test(url.trim());

/** Puts the hero image into the editor DOM, or the solid black band when there is none. */
export function setHero(root: HTMLElement, url: string) {
  const td = root.querySelector<HTMLElement>('td[data-hero]');
  if (!td) return;
  const clean = url.trim();
  td.innerHTML = '';
  if (isHostedImage(clean)) {
    const img = document.createElement('img');
    img.src = clean;
    img.width = 560;
    img.alt = '';
    img.setAttribute('style', 'display:block;width:100%;height:auto;border:0');
    td.appendChild(img);
    td.setAttribute('style', 'padding:0');
  } else {
    td.setAttribute('style', 'padding:0;background:#191919;height:190px');
  }
}

/**
 * Converts the editor DOM into email-safe HTML: no editor attributes, buttons as real links in
 * bulletproof table cells, backgrounds as `bgcolor` + `background-color` (clients drop the
 * `background:` shorthand) plus a same-colour gradient so Gmail's dark mode leaves them alone
 * (unverified, open item #23), and a light-only colour-scheme hint.
 */
export function exportEmailHtml(root: HTMLElement, subject: string): { html: string; warnings: string[] } {
  const warnings: string[] = [];
  const src = root.querySelector<HTMLElement>('table[data-kind]');
  if (!src) return { html: '', warnings: ['Nothing to export'] };
  const t = src.cloneNode(true) as HTMLElement;
  const kind = (src.dataset.kind as EmailKind) || 'rolefit';
  const links = [...LINKS[kind]];

  const hero = t.querySelector<HTMLElement>('td[data-hero]');
  if (hero) {
    const img = hero.querySelector('img');
    if (!img) {
      hero.innerHTML = '&nbsp;';
      hero.setAttribute('style', 'background-color:#191919;height:190px;line-height:190px;font-size:1px;padding:0');
    } else if (!isHostedImage(img.getAttribute('src') || '')) {
      warnings.push('The hero image is not a hosted https URL, so a solid black band is exported instead.');
      hero.innerHTML = '&nbsp;';
      hero.setAttribute('style', 'background-color:#191919;height:190px;line-height:190px;font-size:1px;padding:0');
    }
  }

  t.querySelectorAll<HTMLElement>('[data-btn]').forEach((sp) => {
    const filled = sp.dataset.btn === 'primary';
    const href = links.shift();
    if (!href) return;
    const label = escapeHtml(sp.textContent?.trim() || '');
    const box = document.createElement('table');
    box.setAttribute('role', 'presentation');
    box.setAttribute('cellpadding', '0');
    box.setAttribute('cellspacing', '0');
    box.setAttribute('style', 'display:inline-table;border-collapse:separate');
    const bg = filled ? '#0072EA' : '#FFFFFF';
    box.innerHTML = `<tbody><tr><td bgcolor="${bg}" style="background-color:${bg};border-radius:2px;${filled ? '' : 'border:1px solid #9AA0AD;'}"><a href="${href}" target="_blank" style="display:block;padding:${filled ? '13px 26px' : '12px 25px'};${F};font-weight:600;font-size:14px;line-height:18px;color:${filled ? '#FFFFFF' : '#191919'};text-decoration:none">${label}</a></td></tr></tbody>`;
    sp.replaceWith(box);
  });

  t.querySelectorAll<HTMLElement>('[data-link="portal"]').forEach((sp) => {
    const a = document.createElement('a');
    a.href = '{{portal_link}}';
    a.target = '_blank';
    a.setAttribute('style', sp.getAttribute('style') || LINK);
    a.innerHTML = sp.innerHTML;
    sp.replaceWith(a);
  });

  [t, ...Array.from(t.querySelectorAll<HTMLElement>('*'))].forEach((el) => {
    ['contenteditable', 'data-kind', 'data-hero', 'data-btn', 'data-link', 'class', 'spellcheck'].forEach((a) => el.removeAttribute(a));
    const st = el.getAttribute('style');
    if (!st) return;
    const fixed = st.replace(/(^|;)\s*background\s*:/g, '$1background-color:');
    el.setAttribute('style', fixed);
    const bg = /background-color:\s*(#[0-9A-Fa-f]{3,6})/.exec(fixed);
    if (bg && (el.tagName === 'TD' || el.tagName === 'TABLE') && !el.hasAttribute('bgcolor')) el.setAttribute('bgcolor', bg[1]);
    if (bg && !/background-image/.test(fixed)) {
      el.setAttribute('style', `${fixed.replace(/;?\s*$/, '')};background-image:linear-gradient(${bg[1]},${bg[1]})`);
    }
  });

  const lang = /Hallo|Deine|deine/.test(t.textContent || '') ? 'de' : 'en';
  const white = 'background-color:#FFFFFF;background-image:linear-gradient(#FFFFFF,#FFFFFF)';
  const html =
    `<!DOCTYPE html>\n<html lang="${lang}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="color-scheme" content="light only"><meta name="supported-color-schemes" content="light only"><title>${escapeHtml(subject || 'FINN')}</title><style>:root{color-scheme:light only;supported-color-schemes:light only}</style></head>\n` +
    `<body style="margin:0;padding:0;${white}" bgcolor="#FFFFFF"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" bgcolor="#FFFFFF" style="${white}"><tr><td align="center" bgcolor="#FFFFFF" style="padding:16px 0;${white}">\n` +
    `${t.outerHTML}\n</td></tr></table></body></html>`;
  return { html, warnings };
}
